/**
 * Theme Store
 *
 * Manages theme and appearance preferences.
 * Persists to localStorage and syncs with system preference.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeState {
  // Theme settings
  theme: Theme;
  resolvedTheme: ResolvedTheme;

  // Appearance settings (BUG-008 fix)
  compactMode: boolean;
  animationsEnabled: boolean;

  // Actions
  setTheme: (theme: Theme) => void;
  setCompactMode: (enabled: boolean) => void;
  setAnimationsEnabled: (enabled: boolean) => void;
  initTheme: () => void;
}

// Get system preference
const getSystemTheme = (): ResolvedTheme => {
  if (typeof window === 'undefined') return 'dark';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
};

// Apply theme to document
const applyTheme = (theme: ResolvedTheme) => {
  const root = document.documentElement;

  if (theme === 'light') {
    root.classList.remove('dark');
    root.classList.add('light');
  } else {
    root.classList.remove('light');
    root.classList.add('dark');
  }
};

// Apply compact mode to document
const applyCompactMode = (enabled: boolean) => {
  const root = document.documentElement;
  if (enabled) {
    root.classList.add('compact');
  } else {
    root.classList.remove('compact');
  }
};

// Apply animations setting to document
const applyAnimations = (enabled: boolean) => {
  const root = document.documentElement;
  if (enabled) {
    root.classList.remove('no-animations');
  } else {
    root.classList.add('no-animations');
  }
};

export const useThemeStore = create<ThemeState>()(
  persist(
    (set, get) => ({
      theme: 'dark',
      resolvedTheme: 'dark',
      compactMode: false,
      animationsEnabled: true,

      setTheme: (theme: Theme) => {
        const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;
        applyTheme(resolvedTheme);
        set({ theme, resolvedTheme });
      },

      setCompactMode: (enabled: boolean) => {
        applyCompactMode(enabled);
        set({ compactMode: enabled });
      },

      setAnimationsEnabled: (enabled: boolean) => {
        applyAnimations(enabled);
        set({ animationsEnabled: enabled });
      },

      initTheme: () => {
        const { theme, compactMode, animationsEnabled } = get();
        const resolvedTheme = theme === 'system' ? getSystemTheme() : theme;
        applyTheme(resolvedTheme);
        applyCompactMode(compactMode);
        applyAnimations(animationsEnabled);
        set({ resolvedTheme });

        // Listen for system theme changes
        if (typeof window !== 'undefined') {
          const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
          mediaQuery.addEventListener('change', (e) => {
            const { theme } = get();
            if (theme === 'system') {
              const newResolvedTheme = e.matches ? 'dark' : 'light';
              applyTheme(newResolvedTheme);
              set({ resolvedTheme: newResolvedTheme });
            }
          });
        }
      },
    }),
    {
      name: 'dryvox-theme',
      partialize: (state) => ({
        theme: state.theme,
        compactMode: state.compactMode,
        animationsEnabled: state.animationsEnabled,
      }),
    }
  )
);

export default useThemeStore;
